import { IPoi } from 'src/app/core/poi/poi.model';
import { PaginatedPoi } from './../shared/models/paginated-poi.model';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Poi } from '../core/poi/poi.model';
import { Itin } from '../core/itinerari/itinerari.model';
import { User } from '../core/users/users.model';
import { Themes } from '../core/themes/themes.model';
import { catchError, first, map, tap } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';
import { Attachments } from '../core/attachments/attachments.model';
import { LoggedActionService } from '../core/logged/logged.action';
import { PaginatedItin } from '../shared/models/paginated-itin.model';

@Injectable()
export class ApiService {
  private myToken: string;

  get token(): string {
    return this.myToken;
  }
  set token(token: string) {
    this.myToken = token;
  }

  constructor(private httpClient: HttpClient, private userAction: LoggedActionService) { }

  public createTokenHeader(): HttpHeaders {
    let reqOptions = new HttpHeaders().set('Content-Type', 'application/json')
                                      .set('Accept', 'application/json')
                                      .set('Access-Control-Allow-Origin', '*')
                                      .set('Access-Control-Allow-Methods', 'DELETE, POST, GET, PUT');
    if (!!this.token) {
        reqOptions = reqOptions.set('Authorization', `Bearer ${this.token}`);
    }
    return reqOptions;
  }

  private isNotNullOrUndefined(value: any) {
    return value !== null && value !== undefined;
  }

  // POI REQUESTS

  public getPoi(page: number = 0,
                geographicalPos: number = null,
                thematicPathsIds: number[] = null,
                searchStr: string = null,
                searchLang: string = null,
                approved: boolean = true): Observable<PaginatedPoi> {
    let params = new HttpParams()
                        .set('page', page.toString())
                        .set('approved', approved.toString());
    if (this.isNotNullOrUndefined(geographicalPos)) {
      params = params.set('geopos', geographicalPos.toString());
    }
    if (this.isNotNullOrUndefined(thematicPathsIds) && thematicPathsIds.length !== 0) {
      params = params.set('tags', thematicPathsIds.join(','));
    }
    if (this.isNotNullOrUndefined(searchStr) && searchStr !== '') {
      params = params.set('search', searchStr);
    }
    if (this.isNotNullOrUndefined(searchLang) && searchLang !== '') {
      params = params.set('searchLanguage', searchLang);
    }
    return this.httpClient.get(
      environment.endpoint + 'avatar/poi/all',
      { headers: this.createTokenHeader(), params }
    ).pipe(
      map((response) => PaginatedPoi.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di POI dal server');
        return throwError(err);
      })
    );
  }

  public getPoisForMap(geographicalPos: number): Observable<IPoi[]> {
    let params = new HttpParams().set('geopos', geographicalPos.toString());
    return this.httpClient.get(
      environment.endpoint + 'avatar/poi/map',
      { headers: this.createTokenHeader(), params }
    ).pipe(
      map((response) => Poi.fromArray(response)),
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di POI dal server');
        return throwError(err);
      })
    );
  }

  public getPoiCarousel(): Observable<PaginatedPoi> {
    return this.httpClient.get(
      environment.endpoint + 'avatar/poi/carousel',
      { headers: this.createTokenHeader() }
    ).pipe(
      map((response) => PaginatedPoi.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di POI dal server');
        return throwError(err);
      })
    );
  }

  public getSelectedPoiForItin(geoPosId): Observable<IPoi[]> {
    const params = new HttpParams()
                        .set('geopos', geoPosId.toString());
    return this.httpClient.get(
      environment.endpoint + 'avatar/poi/itin-selection',
      { headers: this.createTokenHeader(), params }
    ).pipe(
      map((response) => Poi.fromArray(response)),
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di POI dal server');
        return throwError(err);
      })
    );
  }

  public poiDetail(id) {
    return this.httpClient.get(
      environment.endpoint + 'avatar/poi/single/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare il singolo POI dal server');
        return throwError(err);
      })
    );
  }

  public editablePoi(id) {
    return this.httpClient.get(
      environment.endpoint + 'avatar/poi/edit/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare il singolo POI dal server');
        return throwError(err);
      })
    );
  }

  public deletePoi = (id) => {
    return this.httpClient.delete(
      environment.endpoint + 'poi/request/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      catchError((err) => {
        console.error('Non è stato possibile eliminare il singolo POI dal server');
        return throwError(err);
      })
    );
  }

  public checkPoiEdits = (id: number) => {
    return this.httpClient.get(
      environment.endpoint + 'poi/request/check-edit/' + id.toString(),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((editArray: any[]) => editArray?.length > 0),
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di modifiche in sospeso');
        return throwError(err);
      })
    );
  }

  public approvePoi(poi: Poi) {
    return this.httpClient.put(
      environment.endpoint + 'avatar/poi/approved',
      Poi.to(poi),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response: any) => ({
        old: response.old,
        poi: Poi.from(response.poi)
      })),
      catchError((err) => {
        console.error('Non è stato possibile approvare il singolo POI');
        return throwError(err);
      })
    );
  }

  public modifyPoi(poi: Poi) {
    return this.httpClient.put(
      environment.endpoint + 'avatar/poi/modify',
      Poi.to(poi),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => Poi.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile modificare il singolo POI');
        return throwError(err);
      })
    );
  }

  public newPoi(poi: Poi) {
    return this.httpClient.post(
      environment.endpoint + 'poi/request/new/',
      Poi.to(poi),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => Poi.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile aggiungere il singolo POI');
        return throwError(err);
      })
    );
  }

  public unapprovePoi(id) {
    return this.httpClient.put(
      environment.endpoint + 'poi/request/unapproved/' + id,
      id,
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      catchError((err) => {
        console.error('Non è stato possibile annullare le modifiche del singolo POI');
        return throwError(err);
      })
    );
  }

  // ITINERARY REQUESTS

  public getItin(page: number = 0,
                 geographicalPos: number = null,
                 thematicPathsIds: number[] = null,
                 searchStr: string = null,
                 searchLang: string = null,
                 approved: boolean = true): Observable<PaginatedItin> {
    let params = new HttpParams()
                .set('page', page.toString())
                .set('approved', approved.toString());
    if (this.isNotNullOrUndefined(geographicalPos)) {
      params = params.set('geopos', geographicalPos.toString());
    }
    if (this.isNotNullOrUndefined(thematicPathsIds) && thematicPathsIds.length !== 0) {
      params = params.set('tags', thematicPathsIds.join(','));
    }
    if (this.isNotNullOrUndefined(searchStr) && searchStr !== '') {
      params = params.set('search', searchStr);
    }
    if (this.isNotNullOrUndefined(searchLang) && searchLang !== '') {
      params = params.set('searchLanguage', searchLang);
    }
    return this.httpClient.get(
      environment.endpoint + 'avatar/itin/all',
      { headers: this.createTokenHeader(), params }
    ).pipe(
      map((response) => PaginatedItin.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di Itinerari dal server');
        return throwError(err);
      })
    );
  }

  public getItinCarousel(): Observable<PaginatedItin> {
    return this.httpClient.get(
      environment.endpoint + 'avatar/itin/carousel',
      { headers: this.createTokenHeader() }
    ).pipe(
      map((response) => PaginatedItin.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di Itinerari dal server');
        return throwError(err);
      })
    );
  }

  public itinDetail(id) {
    return this.httpClient.get(
      environment.endpoint + 'avatar/itin/single/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare il singolo Itinerario dal server');
        return throwError(err);
      })
    );
  }

  public editableItin(id) {
    return this.httpClient.get(
      environment.endpoint + 'avatar/itin/edit/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare il singolo POI dal server');
        return throwError(err);
      })
    );
  }

  public deleteItin = (id) => {
    return this.httpClient.delete(
      environment.endpoint + 'itinerari/request/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Non è stato possibile eliminare il singolo Itinerario dal server');
        return throwError(err);
      })
    );
  }

  public checkItinEdits = (id: number) => {
    return this.httpClient.get(
      environment.endpoint + 'itinerari/request/check-edit/' + id.toString(),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((editArray: any[]) => editArray?.length > 0),
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di modifiche in sospeso');
        return throwError(err);
      })
    );
  }

  public approveItin(itin: Itin) {
    return this.httpClient.put(
      environment.endpoint + 'avatar/itin/approved',
      Itin.to(itin),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response: any) => ({
        old: response.old,
        itin: Itin.from(response.itin)
      })),
      catchError((err) => {
        console.error('Non è stato possibile approvare il singolo Itinerario');
        return throwError(err);
      })
    );
  }

  public modifyItin(itin: Itin) {
    return this.httpClient.put(
      environment.endpoint + 'avatar/itin/modify',
      Itin.to(itin),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => Itin.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile modificare il singolo Itinerario');
        return throwError(err);
      })
    );
  }

  public newItin(itin: Itin) {
    return this.httpClient.post(
      environment.endpoint + 'itinerari/request/new/',
      Itin.to(itin),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => Itin.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile aggiungere il singolo Itinerario');
        return throwError(err);
      })
    );
  }

  public unapproveItin(id) {
    return this.httpClient.put(
      environment.endpoint + 'itinerari/request/unapproved/' + id,
      id,
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      catchError((err) => {
        console.error('Non è stato possibile annullare le modifiche del singolo Itinerario');
        return throwError(err);
      })
    );
  }

  // USER REQUESTS

  public getUser = () => {
    return this.httpClient.get(environment.endpoint + 'user/request', { headers: this.createTokenHeader() }).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di utenti dal server');
        return throwError(err);
      })
    );
  }

  public userDetail(id) {
    return this.httpClient.get(environment.endpoint + 'user/request/' + id, { headers: this.createTokenHeader() }).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare il singolo utente dal server');
        return throwError(err);
      })
    );
  }

  public deleteUser = (id) => {
    return this.httpClient.delete(environment.endpoint + 'user/request/' + id, { headers: this.createTokenHeader() }).pipe(
      catchError((err) => {
        console.error('Non è stato possibile eliminare il singolo utente dal server');
        return throwError(err);
      })
    );
  }

  public loginUser = (credentials) => {
    return this.httpClient.post(
      environment.endpoint + 'user/request/login',
      credentials,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Login fallita');
        return throwError(err);
      })
    );
  }

  public refreshToken() {
    return this.httpClient.get(
      environment.endpoint + 'user/request/refresh-token',
      { headers: this.createTokenHeader() }
    ).pipe(
      tap((result: {token: string}) => {
        this.userAction.refreshToken(result.token);
      }),
      catchError((err) => {
        console.error('Refresh fallito');
        return throwError(err);
      })
    );
  }

  public modifyUser(user: User) {
    return this.httpClient.put(
      environment.endpoint + 'user/request/modify/' + user.id,
      User.to(user),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => User.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile modificare l\'utente');
        return throwError(err);
      })
    );
  }

  public changePwd(id: number, oldPwd: string, newPwd: string) {
    return this.httpClient.post(
      environment.endpoint + 'user/request/modify/password/' + id,
      {old: oldPwd, new: newPwd}, { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => User.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile modificare la password');
        return throwError(err);
      })
    );
  }

  public newUser(user: User) {
    return this.httpClient.post(
      environment.endpoint + 'user/request/new/',
      User.to(user),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => User.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile aggiungere l\'utente sul server');
        return throwError(err);
      })
    );
  }

  // THEME REQUESTS

  public getThemes = () => {
    return this.httpClient.get(environment.endpoint + 'thematicpaths/request', { headers: this.createTokenHeader() }).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare la lista di percorsi tematici dal server');
        return throwError(err);
      })
    );
  }

  public themeDetail(id) {
    return this.httpClient.get(
      environment.endpoint + 'thematicpaths/request/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare il singolo percorso tematico dal server');
        return throwError(err);
      })
    );
  }

  public deleteTheme = (id) => {
    return this.httpClient.delete(
      environment.endpoint + 'thematicpaths/request/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Non è stato possibile eliminare il singolo percorso tematico dal server');
        return throwError(err);
      })
    );
  }

  public modifyTheme(theme: Themes) {
    return this.httpClient.put(
      environment.endpoint + 'thematicpaths/request/modify/' + theme.id,
      Themes.to(theme),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => Themes.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile modificare il percorso tematico');
        return throwError(err);
      })
    );
  }

  public newTheme(theme: Themes) {
    return this.httpClient.post(
      environment.endpoint + 'thematicpaths/request/new/',
      Themes.to(theme),
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => Themes.from(response)),
      catchError((err) => {
        console.error('Non è stato possibile aggiungere il percorso tematico');
        return throwError(err);
      })
    );
  }

  // GEOGRAPHICAL POSITIONS REQUESTS

  public getGeoPos = () => {
    return this.httpClient.get(environment.endpoint + 'geographicalpos/request', { headers: this.createTokenHeader() }).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare le aree dal server');
        return throwError(err);
      })
    );
  }

  public geoposDetail(id) {
    return this.httpClient.get(
      environment.endpoint + 'geographicalpos/request/' + id,
      { headers: this.createTokenHeader() }
    ).pipe(
      catchError((err) => {
        console.error('Non è stato possibile recuperare la singola area dal server');
        return throwError(err);
      })
    );
  }

  // ATTACHMENTS REQUESTS

  public getAttachment(attId: number) {
    return this.httpClient.get(
      environment.endpoint + 'attachment/request/' + attId,
      { headers: this.createTokenHeader() }
    ).pipe(
      first(),
      map((response) => {
        const attachment = Attachments.from(response);
        return attachment.file_byte;
      }),
      catchError((err) => {
        console.error('Non è stato possibile recuperare l\'allegato richiesto');
        return throwError(err);
      })
    );
  }
}
